import { useCameras } from "./useCameras";
import { useEffect, useRef, useState } from "react";
import { Camera, Play, Square } from "lucide-react";
import { upload } from "./api";
export interface CameraAdapter {
  start(
    deviceId?: string,
    facingMode?: "user" | "environment",
  ): Promise<MediaStream>;
  capture(video: HTMLVideoElement): Promise<Blob>;
  stop(): void;
}
export class WebcamAdapter implements CameraAdapter {
  stream?: MediaStream;
  async start(deviceId?: string, facingMode: "user" | "environment" = "user") {
    this.stop();
    if (!navigator.mediaDevices?.getUserMedia)
      throw new Error(
        "Camera access requires localhost or HTTPS. Open this page on the server laptop or enable HTTPS.",
      );
    this.stream = await navigator.mediaDevices.getUserMedia({
      video: {
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        ...(deviceId
          ? { deviceId: { exact: deviceId } }
          : { facingMode: { ideal: facingMode } }),
      },
      audio: false,
    });
    return this.stream;
  }
  async capture(video: HTMLVideoElement) {
    if (!video.videoWidth)
      throw new Error("Wait for the camera preview to become ready.");
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d")!.drawImage(video, 0, 0);
    return new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("Capture failed."))),
        "image/jpeg",
        0.92,
      ),
    );
  }
  stop() {
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = undefined;
  }
}
export function CameraStation({
  eventId,
  onUploaded,
}: {
  eventId: string;
  onUploaded: () => void;
}) {
  const video = useRef<HTMLVideoElement>(null),
    adapter = useRef(new WebcamAdapter()),
    busy = useRef(false);
  const { devices, refresh: refreshCameras } = useCameras();
  const [device, setDevice] = useState(""),
    [started, setStarted] = useState(false),
    [automatic, setAutomatic] = useState(false),
    [seconds, setSeconds] = useState(10),
    [error, setError] = useState(""),
    [count, setCount] = useState(0);
  useEffect(() => () => adapter.current.stop(), []);
  async function start() {
    try {
      setError("");
      const stream = await adapter.current.start(device);
      if (video.current) {
        video.current.srcObject = stream;
        await video.current.play();
      }
      setStarted(true);
      void refreshCameras();
      stream.getVideoTracks()[0]?.addEventListener("ended", () => {
        setStarted(false);
        setAutomatic(false);
        setError("Camera disconnected. Reconnect it or select another camera.");
        void refreshCameras();
      });
    } catch (e) {
      setError((e as Error).message);
      setStarted(false);
    }
  }
  async function capture() {
    if (busy.current || !video.current) return;
    busy.current = true;
    try {
      const blob = await adapter.current.capture(video.current);
      await upload(
        eventId,
        new File([blob], `capture-${Date.now()}.jpg`, { type: "image/jpeg" }),
      );
      setCount((c) => c + 1);
      setError("");
      onUploaded();
    } catch (e) {
      setError((e as Error).message);
      setAutomatic(false);
    } finally {
      busy.current = false;
    }
  }
  useEffect(() => {
    if (!automatic || !started) return;
    const interval = setInterval(() => void capture(), seconds * 1000);
    return () => clearInterval(interval);
  }, [automatic, started, seconds, eventId]);
  return (
    <div className="camera-layout">
      <div className="camera-preview">
        <video ref={video} muted playsInline />
        {!started && (
          <div className="camera-placeholder">
            <Camera size={40} />
            <h3>Your backdrop, connected.</h3>
            <p>Turn your webcam into an automatic photo station.</p>
          </div>
        )}
      </div>
      <div className="panel camera-controls">
        <p className="eyebrow">CAPTURE STATION</p>
        <h2>Ready when you are.</h2>
        <p>Each capture joins this event’s wall automatically.</p>
        <label>
          Camera
          <select
            value={device}
            onChange={(e) => {
              setDevice(e.target.value);
              setAutomatic(false);
              adapter.current.stop();
              setStarted(false);
            }}
          >
            <option value="">Default camera</option>
            {devices.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label || "Camera"}
              </option>
            ))}
          </select>
        </label>
        <button className="primary" onClick={start}>
          {started ? "Reconnect camera" : "Enable camera"}
        </button>
        <button disabled={!started} onClick={() => void capture()}>
          <Camera size={16} /> Take photo
        </button>
        <label>
          Automatic capture interval (seconds)
          <input
            type="number"
            min="3"
            max="300"
            value={seconds}
            onChange={(e) =>
              setSeconds(Math.max(3, Math.min(300, Number(e.target.value))))
            }
          />
        </label>
        <button
          disabled={!started}
          className={automatic ? "danger" : ""}
          onClick={() => setAutomatic(!automatic)}
        >
          {automatic ? <Square size={16} /> : <Play size={16} />}{" "}
          {automatic ? "Stop automatic capture" : "Start automatic capture"}
        </button>
        <p>{count} photos captured this session</p>
        {error && <div className="error">{error}</div>}
        <small>
          External cameras appear when your operating system exposes them as
          webcams. Use a supported webcam mode or capture adapter for
          DSLR/GoPro, or export photos to the watched folder.
        </small>
      </div>
    </div>
  );
}
